import React, {
  useState, useEffect, useMemo, useContext,
} from 'react';
import { useQuery } from '@apollo/client';
import { useSwipeable } from 'react-swipeable';

// import track from 'shared/concerns/tracking/lib/track';
import UserContext from '@context/User/user-context';
import styles from './styles.module.scss';
import Slide from './Slide/Slide';
import { handleSwiping, handleSwiped } from './ForYou.utils';
import { ForYouBoardsQuery, ForYouBoardsDefaultQuery } from './ForYou.graph';

const FOR_YOU_PAGE_SIZE = 10;
const HAVENLY_FOR_YOU_SLIDE_INDEX_STORAGE_KEY = 'HAVENLY_FOR_YOU_SLIDE_INDEX';
const HAVENLY_FOR_YOU_SLIDE_BOARD_ID_STORAGE_KEY = 'HAVENLY_FOR_YOU_SLIDE_BOARD_ID';

const ForYou = (props) => {
  const { user } = useContext(UserContext);
  const { onSaveAsset, savedAssets, setDiscoverScrollPosition } = props;
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [resultsExhausted, setResultsExhausted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideIndex, setSlideIndex] = useState(
    Number(sessionStorage.getItem(HAVENLY_FOR_YOU_SLIDE_INDEX_STORAGE_KEY)) || 0,
  );
  const [pageNumber, setPageNumber] = useState(Math.ceil((slideIndex + 1) / FOR_YOU_PAGE_SIZE));
  const listId = 'for_you';

  const initialPageSize = useMemo(() => {
    return (
      Math.ceil((slideIndex + 1) / FOR_YOU_PAGE_SIZE) * FOR_YOU_PAGE_SIZE
    ) || FOR_YOU_PAGE_SIZE;
  }, []);

  useEffect(() => {
    setPageNumber(Math.ceil((slideIndex + 1) / FOR_YOU_PAGE_SIZE) || 1);
  }, []);

  let Query = ForYouBoardsDefaultQuery;
  const variables = {
    pageNumber: 1,
    pageSize: initialPageSize,
  };
  if (user) {
    Query = ForYouBoardsQuery;
    variables.userId = user.id;
  }

  const { data, fetchMore } = useQuery(Query, {
    variables,
    onCompleted: ({ forYouBoards }) => {
      if (forYouBoards?.items?.length < initialPageSize) {
        setResultsExhausted(true);
      }
    },
  });

  let boards = [];
  if (data && data.forYouBoards) {
    boards = data.forYouBoards.items;
  }

  const swipeHandlers = useSwipeable({
    onSwiping: handleSwiping(setOffset),
    onSwiped: handleSwiped(
      setIsAnimating,
      setSlideIndex,
      slideIndex,
      setOffset,
      offset,
      boards,
      setDiscoverScrollPosition,
    ),
    preventDefaultTouchmoveEvent: true,
    delta: 1, // min distance(px) before a swipe starts
  });

  useEffect(() => {
    if (
      boards.length > 0
      && slideIndex > Math.max(0, (pageNumber * FOR_YOU_PAGE_SIZE) - (FOR_YOU_PAGE_SIZE / 2))
      && boards.length < ((pageNumber + 1) * FOR_YOU_PAGE_SIZE)
      && !resultsExhausted
    ) {
      const newPageNumber = pageNumber + 1;

      fetchMore({
        variables: {
          pageNumber: newPageNumber,
          pageSize: FOR_YOU_PAGE_SIZE,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (
            !fetchMoreResult
            || !fetchMoreResult.forYouBoards
            || !prev
            || !prev.forYouBoards
            || fetchMoreResult.forYouBoards.pageNumber ===
            prev.forYouBoards.pageNumber
          ) {
            return prev;
          }

          if (fetchMoreResult.forYouBoards.items.length < FOR_YOU_PAGE_SIZE) {
            setResultsExhausted(true);
          }

          return {
            ...prev,
            forYouBoards: {
              ...fetchMoreResult.forYouBoards,
              items: [
                ...prev.forYouBoards.items,
                ...fetchMoreResult.forYouBoards.items,
              ],
            },
          };
        },
      });

      setPageNumber(newPageNumber);
    }

    const board = boards[slideIndex];
    if (board) {
      sessionStorage.setItem(HAVENLY_FOR_YOU_SLIDE_BOARD_ID_STORAGE_KEY, board.id);
      /*
      track.exploreDesignerBoardViewed({
        designer_id: board.designer?.id || 0,
        board_id: board.id,
        list_id: listId,
        style_array: board.subStyles?.map(({ name }) => name).join(', ') || [],
      });
*/
    }

    sessionStorage.setItem(HAVENLY_FOR_YOU_SLIDE_INDEX_STORAGE_KEY, slideIndex);
  }, [slideIndex]);

  // Safety check, reset the slide index if its higher than the final results count
  if (resultsExhausted && slideIndex > boards.length) {
    setSlideIndex(0);
    sessionStorage.setItem(HAVENLY_FOR_YOU_SLIDE_INDEX_STORAGE_KEY, 0);
  }

  if (!boards) {
    return null;
  }

  return (
    <div
      className={ styles.ForYou }
      { ...swipeHandlers }
    >
      <Slide
        board={ boards[slideIndex - 1] }
        first
        isAnimating={ isAnimating }
        listId={ listId }
        offset={ offset }
        onSaveAsset={ onSaveAsset }
        savedAssets={ savedAssets }
      />
      <Slide
        board={ boards[slideIndex] }
        isAnimating={ isAnimating }
        listId={ listId }
        offset={ offset }
        onSaveAsset={ onSaveAsset }
        savedAssets={ savedAssets }
      />
      <Slide
        board={ boards[slideIndex + 1] }
        isAnimating={ isAnimating }
        last
        listId={ listId }
        offset={ offset }
        onSaveAsset={ onSaveAsset }
        savedAssets={ savedAssets }
      />
    </div>
  );
};
export default ForYou;
